import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Path } from 'constants/router'
import { RealEstatePropertyAssignmentDTO } from 'models/property'
import constate from 'constate'
import { getPropertyAssignmentsByTab } from 'utils/helpers/getPropertyAssignmentsByTab'
import { reorderClickProps, settleReorderContext, withReorderContext } from 'utils/analytics/reorderClick'
import { useGetPropertyReorderContext, usePropertyReorderContextResolver } from 'dataQueries/property.query'
import { startOrderAttempt } from 'utils/analytics/orderAttempt'
import { useCanUserOrder } from 'utils/user/canUserOrder'
import { useGetCategoryConfig$ } from 'dataQueries/purchase.query'
import { useGetPropertyDetail } from 'dataQueries'
import { useNavigate } from 'react-router-dom'
import { useUserData } from 'components/contexts/UserDataContext'

export enum PropertyTab {
  GALLERY = 'gallery', // DEFAULT tab
  DOCUMENTS = 'documents',
  AI_TEXT_GENERATOR = 'ai_text_generator',
  MARKETING_CAMPAIGN = 'marketing_campaign', // behind `marketing-automation` (ISSUE-3615)
  ORDERS = 'orders',
  INFORMATION = 'information',
}

export const propertyTabs = new Set([
  PropertyTab.GALLERY,
  PropertyTab.DOCUMENTS,
  PropertyTab.AI_TEXT_GENERATOR,
  PropertyTab.MARKETING_CAMPAIGN,
  PropertyTab.ORDERS,
  PropertyTab.INFORMATION,
])

export const [ClientPropertyContextProvider, useClientProperty] = constate(({
  propertyId,
  resolvedWorkspaceId,
  isPropertyWorkspaceResolved,
  tab = PropertyTab.GALLERY,
  assignmentId,
  propertyAssignmentsData
}: {
  propertyId: string
  resolvedWorkspaceId: string
  /** Whether the property's workspace was read: `resolvedWorkspaceId` is final, no longer '' while it is on its way */
  isPropertyWorkspaceResolved: boolean
  tab: string
  assignmentId?: string
  propertyAssignmentsData: RealEstatePropertyAssignmentDTO[] | null
}) => {
  const navigate = useNavigate()
  const { baseUserData, currentUserWorkspace } = useUserData()

  const [activeTab, setActiveTab] = useState<PropertyTab>(tab as PropertyTab)
  const [isPropertyAddressPrefilled, setIsPropertyAddressPrefilled] = useState<boolean | null>(null)

  // Use the property's resolved workspace (passed from ClientProperty.page) so detail/orders fetches
  // hit the workspace that actually owns the property, not the globally selected one. See ISSUE-2217.
  const getPropertyDetailQuery = useGetPropertyDetail(propertyId, resolvedWorkspaceId, false)
  const propertyData = useMemo(() => getPropertyDetailQuery.data, [getPropertyDetailQuery.data])

  // Read ahead for the order CTAs, gated like them
  const canUserOrder = useCanUserOrder()
  useGetPropertyReorderContext(propertyId, resolvedWorkspaceId, canUserOrder)
  const resolveReorderContext = usePropertyReorderContextResolver()

  const getCategoryConfig = useGetCategoryConfig$()
  const sessionId = useMemo(() => getCategoryConfig.data?.data.sessionId ?? null, [getCategoryConfig.data?.data.sessionId])

  const handleTabChange = (selectedTab: PropertyTab) => {
    if (selectedTab === PropertyTab.GALLERY || selectedTab === PropertyTab.DOCUMENTS) {
      const firstAssignmentId = getPropertyAssignmentsByTab(selectedTab, propertyAssignmentsData)[0]?.id

      if (firstAssignmentId) navigate(Path.PROPERTY_TAB_ASSIGNMENT
        .replace(':id', propertyId.toString())
        .replace(':tab', selectedTab ?? PropertyTab.GALLERY)
        .replace(':assignmentId', firstAssignmentId))

      else navigate(Path.PROPERTY_TAB
        .replace(':id', propertyId.toString())
        .replace(':tab', selectedTab ?? PropertyTab.GALLERY)
      )
    } else {
      navigate(Path.PROPERTY_TAB.replace(':id', propertyId.toString()).replace(':tab', selectedTab))
    }

    let eventName = AnalyticsEvent.CLICK_GALLERY_TAB

    switch (selectedTab) {
      case PropertyTab.DOCUMENTS:
        eventName = AnalyticsEvent.CLICK_PROPERTY_DOCUMENTS
        break
      case PropertyTab.AI_TEXT_GENERATOR:
        eventName = AnalyticsEvent.CLICK_PROPERTY_AI_TEXT_GENERATOR_TAB
        break
      case PropertyTab.MARKETING_CAMPAIGN:
        eventName = AnalyticsEvent.CLICK_PROPERTY_MARKETING_CAMPAIGN_TAB
        break
      case PropertyTab.ORDERS:
        eventName = AnalyticsEvent.CLICK_PROPERTY_ORDERS_TAB
        break
      case PropertyTab.INFORMATION:
        eventName = AnalyticsEvent.CLICK_PROPERTY_INFORMATION_TAB
        break
      default:
        eventName = AnalyticsEvent.CLICK_PROPERTY_VISUALS_GALLERY_TAB
        break
    }

    if (eventName) {
      logAnalyticsEvent(eventName, {
        propertyId: propertyData?.id,
        realtyId: propertyData?.id,
        propertyName: propertyData?.title,
        propertyType: propertyData?.propertyType,
        planId: currentUserWorkspace?.id,
      })
    }
  }

  // Every caller opens the purchase flow for this property, so the click starts the attempt the flow adopts;
  // the event logs before the flow opens when the context is cached, after the read otherwise
  const handleClickOnEmptyAction = useCallback((eventName: AnalyticsEvent) => {
    const orderAttemptId = startOrderAttempt('property_page')
    const reorderContext = resolvedWorkspaceId ? settleReorderContext(resolveReorderContext({ workspaceId: resolvedWorkspaceId, propertyId })) : null
    withReorderContext(reorderContext, (context, clickTime) => {
      logAnalyticsEvent(eventName, {
        propertyName: propertyData?.title,
        propertyId: propertyData?.id,
        realtyId: propertyData?.id,
        propertyType: propertyData?.propertyType,
        planId: currentUserWorkspace?.id,
        orderAttemptId,
        ...reorderClickProps('property_page', context),
        ...clickTime,
      })
    })
  }, [propertyData?.id, propertyData?.propertyType, propertyData?.title, currentUserWorkspace?.id, resolveReorderContext, resolvedWorkspaceId, propertyId])

  const showPropertyNotFound = useMemo(() => !propertyData, [propertyData])

  const propertyFullAddress = useMemo(() => {
    const address = propertyData?.address

    if (!address) return '-' // edge case

    return `${address.street} ${address.streetNumber}, ${address.city}, ${address.country}`
  }, [propertyData?.address])

  const selectedPropertyAssignment = useMemo(() => {
    return propertyAssignmentsData?.find((assignment) => assignment.id === assignmentId)
  }, [assignmentId, propertyAssignmentsData])

  const isSelectedPropertyAssignmentCancelled = useMemo(() => selectedPropertyAssignment?.isCancelled === true, [selectedPropertyAssignment])

  // Prefill data on realtyId
  useEffect(() => {
    if (isPropertyAddressPrefilled !== null) return

    if (!propertyId || (getPropertyDetailQuery.isSuccess && !propertyData?.address)) {
      setIsPropertyAddressPrefilled(false)
      return
    } else {
      setIsPropertyAddressPrefilled(true)
    }
    // Omit "isPropertyAddressPrefilled"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, propertyData, getPropertyDetailQuery])

  /** Fetch the category config to get session id */
  useEffect(() => {
    const userEmail = baseUserData?.email
    if (!userEmail) return
    if (!getCategoryConfig.isIdle) return

    getCategoryConfig.mutate({ email: userEmail })
  }, [baseUserData?.email, getCategoryConfig])

  useEffect(() => {
    setActiveTab(tab as PropertyTab)
  }, [tab])

  return {
    sessionId,
    propertyId,
    resolvedWorkspaceId,
    isPropertyWorkspaceResolved,
    propertyData,
    propertyAssignmentsData,
    handleTabChange,
    activeTab,
    setActiveTab,
    assignmentId,
    showPropertyNotFound,
    propertyFullAddress,
    isPropertyAddressPrefilled,
    handleClickOnEmptyAction,
    getCategoryConfig,
    selectedPropertyAssignment,
    isSelectedPropertyAssignmentCancelled,
  }
})
